/**
 * MyCopro - API Chat
 * Avec création automatique des groupes
 * 
 * ✅ AJOUT: deleteChatMessage() — soft delete d'un message
 */

import { db } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  setDoc,
  addDoc, 
  updateDoc,
  query, 
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { ChatGroup, ChatMessage, ChatGroupId, UserCategoryType } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// DÉFINITIONS DES GROUPES
// ============================================================================

export interface ChatGroupDefinition {
  id: string;
  name: string;
  type: 'residence' | 'owners' | 'council';
  description: string;
  allowedCategories: UserCategoryType[];
}

export const CHAT_GROUP_DEFINITIONS: ChatGroupDefinition[] = [
  {
    id: 'residence',
    name: 'Résidence',
    type: 'residence',
    description: 'Discussion ouverte à tous les résidents',
    allowedCategories: ['tenant', 'owner', 'council', 'syndic', 'gardien', 'admin']
  },
  {
    id: 'owners',
    name: 'Copropriétaires',
    type: 'owners',
    description: 'Discussion réservée aux copropriétaires',
    allowedCategories: ['owner', 'council', 'syndic', 'admin']
  },
  {
    id: 'council',
    name: 'Conseil Syndical',
    type: 'council',
    description: 'Discussion réservée au conseil syndical',
    allowedCategories: ['council', 'syndic', 'admin']
  }
];

// ============================================================================
// CRÉATION DES GROUPES DE CHAT
// ============================================================================

/**
 * Créer un groupe de chat s'il n'existe pas
 */
export const createChatGroupIfNotExists = async (
  residenceId: string,
  groupDef: ChatGroupDefinition
): Promise<boolean> => {
  try {
    const groupRef = doc(db, 'residences', residenceId, 'chatGroups', groupDef.id);
    const groupSnap = await getDoc(groupRef);

    if (!groupSnap.exists()) {
      await setDoc(groupRef, {
        name: groupDef.name,
        type: groupDef.type,
        description: groupDef.description,
        allowedCategories: groupDef.allowedCategories,
        createdAt: serverTimestamp(),
        lastMessage: '',
        lastMessageTime: null,
        lastSenderName: '',
        lastSenderId: ''
      });
      logger.db(`Chat group "${groupDef.id}" created for residence ${residenceId}`);
      return true;
    }
    
    return false; // Already exists
  } catch (error) {
    console.error(`Error creating chat group "${groupDef.id}":`, error);
    throw error;
  }
};

/**
 * Créer tous les groupes de chat pour une résidence
 * Appelé lors de la création d'une résidence
 */
export const createAllChatGroups = async (residenceId: string): Promise<void> => {
  logger.db(`Creating all chat groups for residence ${residenceId}...`);
  
  for (const groupDef of CHAT_GROUP_DEFINITIONS) {
    await createChatGroupIfNotExists(residenceId, groupDef);
  }
  
  logger.db('All chat groups created successfully');
};

/**
 * S'assurer qu'un groupe de chat spécifique existe
 * Appelé lors de l'activation d'un chat dans les paramètres
 */
export const ensureChatGroupExists = async (
  residenceId: string,
  groupType: 'residence' | 'owners' | 'council'
): Promise<void> => {
  const groupDef = CHAT_GROUP_DEFINITIONS.find(g => g.type === groupType);
  
  if (!groupDef) {
    console.error(`Unknown chat group type: ${groupType}`);
    return;
  }
  
  await createChatGroupIfNotExists(residenceId, groupDef);
};

/**
 * S'assurer que tous les groupes activés existent
 * Appelé lors de la sauvegarde des paramètres de résidence
 */
export const ensureActiveChatGroupsExist = async (
  residenceId: string,
  chatSettings: { residence?: boolean; owners?: boolean; council?: boolean }
): Promise<void> => {
  const promises: Promise<void>[] = [];
  
  if (chatSettings.residence) {
    promises.push(ensureChatGroupExists(residenceId, 'residence'));
  }
  if (chatSettings.owners) {
    promises.push(ensureChatGroupExists(residenceId, 'owners'));
  }
  if (chatSettings.council) {
    promises.push(ensureChatGroupExists(residenceId, 'council'));
  }
  
  await Promise.all(promises);
};

// ============================================================================
// RÉCUPÉRATION DES GROUPES
// ============================================================================

/**
 * Récupérer les groupes de chat accessibles pour un utilisateur
 */
export const getChatGroupsForUser = async (
  residenceId: string, 
  userCategory: UserCategoryType
): Promise<ChatGroup[]> => {
  try {
    // Définir les accès par catégorie
    const accessMap: Record<UserCategoryType, ChatGroupId[]> = {
      tenant: ['residence'],
      owner: ['residence', 'owners'],
      council: ['residence', 'owners', 'council'],
      syndic: ['residence', 'owners', 'council'],
      gardien: ['residence'],
      admin: ['residence', 'owners', 'council']
    };

    const allowedGroups = accessMap[userCategory] || ['residence'];

    const groupsRef = collection(db, 'residences', residenceId, 'chatGroups');
    const querySnapshot = await getDocs(groupsRef);

    const groups: ChatGroup[] = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      if (allowedGroups.includes(doc.id as ChatGroupId)) {
        groups.push({
          id: doc.id as ChatGroupId,
          name: data.name || doc.id,
          lastMessage: data.lastMessage || null
        } as ChatGroup);
      }
    });

    return groups;
  } catch (error) {
    console.error('Error getting chat groups:', error);
    return [];
  }
};

// ============================================================================
// MESSAGES
// ============================================================================

/**
 * Récupérer les messages d'un groupe
 */
export const getChatMessages = async (
  residenceId: string, 
  groupId: string,
  limitCount: number = 50
): Promise<ChatMessage[]> => {
  try {
    const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);

    const messages: ChatMessage[] = [];
    querySnapshot.forEach(doc => {
      messages.push({
        id: doc.id,
        ...doc.data()
      } as ChatMessage);
    });

    return messages.reverse(); // Plus ancien en premier
  } catch (error) {
    console.error('Error getting chat messages:', error);
    return [];
  }
};

/**
 * Envoyer un message
 */
export const sendChatMessage = async (
  residenceId: string,
  groupId: string,
  message: Omit<ChatMessage, 'id'>
): Promise<string> => {
  try {
    const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
    const docRef = await addDoc(messagesRef, {
      ...message,
      deleted: false, // ✅ Initialisé à false
      createdAt: serverTimestamp()
    });

    // Mettre à jour lastMessage du groupe
    const groupRef = doc(db, 'residences', residenceId, 'chatGroups', groupId);
    await updateDoc(groupRef, {
      lastMessage: message.text?.substring(0, 100) || '',
      lastSenderName: message.senderName || '',
      lastSenderId: message.sender || '',
      lastMessageTime: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * ✅ Soft delete d'un message
 * Marque le message comme supprimé sans le retirer de Firestore.
 * Vérifie que le senderId correspond bien à l'userId fourni.
 */
export const deleteChatMessage = async (
  residenceId: string,
  groupId: string,
  messageId: string,
  userId: string
): Promise<void> => {
  try {
    const messageRef = doc(
      db, 'residences', residenceId, 'chatGroups', groupId, 'messages', messageId
    );
    const messageSnap = await getDoc(messageRef);

    if (!messageSnap.exists()) {
      throw new Error('Message introuvable');
    }

    const data = messageSnap.data();

    // Sécurité : seul l'auteur peut supprimer son message
    if (data.senderId !== userId) {
      throw new Error('Vous ne pouvez supprimer que vos propres messages');
    }

    if (data.deleted) {
      return; // Déjà supprimé, rien à faire
    }

    await updateDoc(messageRef, {
      deleted: true,
      deletedAt: serverTimestamp(),
      text: ''
    });
  } catch (error) {
    console.error('Error deleting chat message:', error);
    throw error;
  }
};

export const markMessagesAsRead = async (
  residenceId: string,
  groupId: string,
  userId: string
): Promise<void> => {
  try {
    const memberRef = doc(db, 'members', userId);
    await updateDoc(memberRef, {
      [`lastReadAt.${groupId}`]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * S'abonner aux nouveaux messages (temps réel)
 */
export const subscribeToMessages = (
  residenceId: string,
  groupId: string,
  callback: (messages: ChatMessage[]) => void
): (() => void) => {
  const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
  const q = query(messagesRef, orderBy('createdAt', 'asc'));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const messages: ChatMessage[] = [];
    snapshot.forEach(doc => {
      messages.push({
        id: doc.id,
        ...doc.data()
      } as ChatMessage);
    });
    callback(messages);
  });

  return unsubscribe;
};

/**
 * Récupérer le nombre de messages non lus
 */
export const getUnreadCount = async (
  residenceId: string,
  groupId: string,
  userId: string
): Promise<number> => {
  try {
    // Simplification: retourner 0 pour l'instant
    return 0;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};
